import * as yup from "yup";
import type { TestContext } from "yup";
import type { CounselingCategory } from "./service/expert.type";

const officeInfoSchema = yup.object().shape({
    name: yup.string().required("중개사무소 정보를 입력해주세요."),
    ceo: yup.string().required("중개사무소 정보를 입력해주세요."),
});

export const expertFormSchema = yup
    .object()
    .shape({
        counselingCategories: yup
            .array()
            .of(yup.string() as yup.StringSchema<CounselingCategory>)
            .min(1, "전문가 유형을 하나 이상 선택해주세요."),

        careerObj: yup
            .object()
            .nullable()
            .default({})
            .test(
                "careerObj",
                "경력을 올바르게 입력해주세요.",
                function (this: TestContext, careerObj: Record<string, number | string> | null) {
                    const categories = this.parent?.counselingCategories as CounselingCategory[] | undefined;
                    if (!categories?.length) return true;

                    const obj = careerObj ?? {};
                    const currentYear = new Date().getFullYear();

                    for (const category of categories) {
                        const year = Number(obj[category]);
                        if (!year) {
                            return this.createError({ message: "경력을 올바르게 입력해주세요." });
                        }
                        if (year < 1000 || year > currentYear) {
                            return this.createError({ message: "입력하신 경력 연도가 유효하지 않습니다." });
                        }
                    }
                    return true;
                },
            ),

        bankName: yup.string().required("은행명과 계좌번호를 모두 입력해주세요."),
        bankAccount: yup.string().required("은행명과 계좌번호를 모두 입력해주세요."),

        officeInfo: yup
            .object()
            .nullable()
            .when("counselingCategories", {
                is: (cats: CounselingCategory[] | undefined) =>
                    Array.isArray(cats) && (cats.includes("중개") || cats.includes("매수신청대리인")),
                then: () => officeInfoSchema.required("중개사무소 정보를 입력해주세요."),
                otherwise: (schema: yup.AnySchema) => schema.nullable(),
            }),

        auctionAgentCertFileUrl: yup
            .string()
            .nullable()
            .when("counselingCategories", {
                is: (cats: CounselingCategory[] | undefined) => Array.isArray(cats) && cats.includes("매수신청대리인"),
                then: (schema: yup.AnySchema) => schema.required("매수신청대리인 등록증을 업로드해주세요."),
                otherwise: (schema: yup.AnySchema) => schema.nullable(),
            }),
    })
    .required();

export type ExpertInfoForValidation = yup.InferType<typeof expertFormSchema>;

export function validateExpertForm(info: Partial<ExpertInfoForValidation>): { valid: boolean; error?: string } {
    try {
        expertFormSchema.validateSync(info, { abortEarly: true });
        return { valid: true };
    } catch (err: unknown) {
        if (err instanceof yup.ValidationError) {
            const firstError = err.errors?.[0];
            return { valid: false, error: typeof firstError === "string" ? firstError : "입력값을 확인해주세요." };
        }
        return { valid: false, error: "입력값을 확인해주세요." };
    }
}
